import { useCallback, useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { BASELINE_DAYS } from './constants';
import { helpText, Help } from './help';
import { formatShortDate, formatMetric } from './format';
import { isMissing, usualRange, verdictOf, runsBefore, sameDevices } from './metrics';
import { LegendToggle, useHiddenSet, TREND_COLORS, sdkVersionMarkers, versionMarkers } from './charts';
import { appChangeAt, appChangeTooltipLines } from './appDiff';
import { AppChangeDialog } from './AppChangeDialog';

const isUnusual = (verdict) => verdict === 'above' || verdict === 'below';

function buildCostTrend(entries, kpi, showRaw) {
  const values = entries.map(({ summary }) => kpi.read(summary));
  const ranges = entries.map(({ run }, index) => {
    const earlier = runsBefore(entries.slice(0, index).map((entry) => entry.run), run);
    const earlierIds = new Set(earlier.map((other) => other.id));
    return usualRange(entries
      .filter((entry) => earlierIds.has(entry.run.id) && sameDevices(kpi, entry.summary, entries[index].summary))
      .map((entry) => kpi.read(entry.summary)));
  });
  const verdicts = values.map((value, index) => verdictOf(value, ranges[index]));
  const pointColor = (verdict) => {
    if (verdict === 'above') return TREND_COLORS.worse;
    if (verdict === 'below') return TREND_COLORS.better;
    return TREND_COLORS.cost;
  };
  const band = (bound) => ranges.map((range) => (isMissing(range?.low) ? null : range[bound]));
  const datasets = [
    {
      label: 'Usual range (high)', legendKey: 'band', data: band('high'), band: true, borderWidth: 0, pointRadius: 0, fill: '+1', backgroundColor: TREND_COLORS.band, unit: kpi.unit,
    },
    {
      label: 'Usual range (low)', legendKey: 'band', data: band('low'), band: true, borderWidth: 0, pointRadius: 0, fill: false, unit: kpi.unit,
    },
    {
      label: kpi.title,
      legendKey: 'cost',
      data: values,
      borderColor: TREND_COLORS.cost,
      backgroundColor: TREND_COLORS.cost,
      borderWidth: 2.5,
      tension: 0.25,
      pointRadius: verdicts.map((verdict) => (isUnusual(verdict) ? 5 : 3)),
      pointBackgroundColor: verdicts.map(pointColor),
      pointBorderColor: verdicts.map(pointColor),
      spanGaps: true,
      unit: kpi.unit,
      signed: kpi.signed,
    },
  ];
  if (showRaw && kpi.raw) {
    const figures = entries.map(({ summary }) => summary?.figures?.[kpi.metric]);
    datasets.push(
      {
        label: `${kpi.raw.label} with SDK`, legendKey: 'raw-with', data: figures.map((figure) => figure?.enabled ?? null), borderColor: TREND_COLORS.raw, backgroundColor: TREND_COLORS.raw, borderWidth: 1.5, pointRadius: 0, unit: kpi.raw.unit,
      },
      {
        label: `${kpi.raw.label} without SDK`, legendKey: 'raw-without', data: figures.map((figure) => figure?.disabled ?? null), borderColor: TREND_COLORS.raw, backgroundColor: TREND_COLORS.raw, borderWidth: 1.5, borderDash: [5, 4], pointRadius: 0, unit: kpi.raw.unit,
      },
    );
  }
  return {
    data: { labels: entries.map(({ run }) => formatShortDate(run.uploadedAt)), datasets },
    markers: versionMarkers(entries.map(({ summary }) => summary?.sdkVersion)),
    values,
    verdicts,
  };
}

export function CostTrend({ entries, allRuns, kpis, kpi, platform, onSelect, showRaw, onShowRaw, loading }) {
  const { data, markers, values, verdicts } = useMemo(() => buildCostTrend(entries, kpi, showRaw), [entries, kpi, showRaw]);
  const [hidden, toggle] = useHiddenSet();
  const [selectedIndex, setSelectedIndex] = useState(null);
  useEffect(() => setSelectedIndex(null), [entries, kpi.key]);
  const runs = useMemo(() => entries.map(({ run }) => run), [entries]);
  const unusualAt = (index) => index !== undefined && index !== null && isUnusual(verdicts[index]);
  const closeChange = useCallback(() => setSelectedIndex(null), []);
  const changeAt = (index) => appChangeAt(runs, values, index, allRuns || runs);
  const selectedChange = unusualAt(selectedIndex) ? changeAt(selectedIndex) : null;
  const shownData = useMemo(() => ({
    ...data,
    datasets: data.datasets.map((dataset) => ({ ...dataset, hidden: hidden.has(dataset.legendKey) })),
  }), [data, hidden]);
  const hasData = data.datasets.some((dataset) => !dataset.band && dataset.data.some((value) => !isMissing(value)));
  return (
    <div className="sdkp-panel sdkp-section-spaced" data-panel="cost-trend">
      <div className="sdkp-panel-head">
        <h2 className="sdkp-panel-title">How the SDK&apos;s cost changed over time</h2>
        <div className="sdkp-segmented" aria-label="Trend metric">
          {kpis.map((item) => (
            <button key={item.key} type="button" className={item.key === kpi.key ? 'sdkp-active' : ''} onClick={() => onSelect(item.key)}>
              {item.tab}
            </button>
          ))}
        </div>
      </div>
      <p className="sdkp-panel-hint">
        One point per nightly run. Shaded band = usual range of the previous {BASELINE_DAYS} days. Red = unusually high, green = unusually low. Dotted lines = a new SDK version. Hover a red or green point to see what changed in the test app since the run before, click it for the full list.
      </p>
      {loading && <div className="sdkp-empty">Loading…</div>}
      {!loading && !hasData && <div className="sdkp-empty">No {kpi.tab} trend data for the selected filters.</div>}
      {!loading && hasData && (
        <div className="sdkp-chart sdkp-chart-tall">
          <Line
            data={shownData}
            plugins={[sdkVersionMarkers]}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: { mode: 'index', intersect: false },
              onClick: (event, elements) => {
                const index = elements?.[0]?.index;
                setSelectedIndex(unusualAt(index) ? index : null);
              },
              onHover: (event, elements) => {
                const target = event?.native?.target;
                if (target) target.style.cursor = unusualAt(elements?.[0]?.index) ? 'pointer' : 'default';
              },
              plugins: {
                legend: { display: false },
                sdkVersionMarkers: { markers: hidden.has('versions') ? [] : markers },
                tooltip: {
                  filter: (item) => !item.dataset.band,
                  footerFont: { weight: 'normal' },
                  callbacks: {
                    label: (context) => `${context.dataset.label}: ${formatMetric(context.parsed.y, context.dataset.unit, context.dataset.signed)}`,
                    footer: (items) => {
                      const index = items?.[0]?.dataIndex;
                      return unusualAt(index) ? appChangeTooltipLines(changeAt(index)) : [];
                    },
                  },
                },
              },
              scales: {
                x: { ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 8 } },
                y: {
                  beginAtZero: true,
                  title: { display: true, text: kpi.axisTitle, font: { size: 11, weight: 'bold' } },
                },
              },
            }}
          />
        </div>
      )}
      {!loading && hasData && <AppChangeDialog change={selectedChange} onClose={closeChange} />}
      <div className="sdkp-legend">
        <LegendToggle hidden={hidden.has('cost')} onToggle={() => toggle('cost')} swatchStyle={{ background: TREND_COLORS.cost }}>{kpi.title}</LegendToggle>
        <LegendToggle hidden={hidden.has('band')} onToggle={() => toggle('band')} swatchStyle={{ background: TREND_COLORS.band }}>Usual range</LegendToggle>
        <LegendToggle hidden={hidden.has('versions')} onToggle={() => toggle('versions')} swatchClassName="sdkp-swatch-dashed">New SDK version</LegendToggle>
        {showRaw && kpi.raw && (
          <>
            <LegendToggle hidden={hidden.has('raw-with')} onToggle={() => toggle('raw-with')} swatchStyle={{ background: TREND_COLORS.raw }}>{kpi.raw.label} with SDK</LegendToggle>
            <LegendToggle hidden={hidden.has('raw-without')} onToggle={() => toggle('raw-without')} swatchClassName="sdkp-swatch-dashed sdkp-swatch-raw">{kpi.raw.label} without SDK</LegendToggle>
          </>
        )}
        {kpi.raw && (
          <span className="sdkp-raw-toggle">
            <label>
              <input type="checkbox" checked={showRaw} onChange={(event) => onShowRaw(event.target.checked)} />
              {` Show the raw ${kpi.raw.label} with and without the SDK`}
            </label>
            <Help text={helpText('raw', platform)} align="right" />
          </span>
        )}
      </div>
    </div>
  );
}
